/* PhotoUploadField — small uploader that posts images to
 * /api/cases/{id}/upload?kind=<kind> and tracks the resulting file_ids.
 *
 * Used by:
 *  - Production stage data dialogs (attaches per-stage evidence)
 *  - Production escalate dialog (attaches evidence on flagged issues)
 *
 * Designed for low ceremony — pick image(s), they upload immediately,
 * the caller receives the file_id list via onChange. No previews of the
 * remote URL; we render thumbnails from the local File objects until
 * unmount.
 */
import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, X, Image as ImageIcon, Spinner } from "@phosphor-icons/react";


export default function PhotoUploadField({
    caseId,
    kind = "production_attachment",
    value = [],
    onChange,
    disabled,
    label = "Photo attachments",
    helperText = "Tap to attach photos that help admin triage faster.",
    max = 4,
    testid = "photo-upload-field",
}) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [thumbs, setThumbs] = useState([]); // { file_id, name, previewUrl }

    const upload = async (files) => {
        if (!files || files.length === 0) return;
        if (!caseId) {
            toast.error("Case not loaded yet — try again in a second.");
            return;
        }
        const remaining = max - value.length;
        const slice = Array.from(files).slice(0, Math.max(0, remaining));
        if (slice.length === 0) {
            toast.error(`Max ${max} photos`);
            return;
        }
        setUploading(true);
        const newIds = [];
        const newThumbs = [];
        try {
            for (const file of slice) {
                const fd = new FormData();
                fd.append("file", file);
                const r = await api.post(
                    `/cases/${caseId}/upload?kind=${kind}`,
                    fd,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
                if (r.data?.id) {
                    newIds.push(r.data.id);
                    newThumbs.push({
                        file_id: r.data.id,
                        name: file.name,
                        previewUrl: URL.createObjectURL(file),
                    });
                }
            }
            if (newIds.length > 0) {
                onChange?.([...(value || []), ...newIds]);
                setThumbs((p) => [...p, ...newThumbs]);
                toast.success(`${newIds.length} photo${newIds.length === 1 ? "" : "s"} attached`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = (fid) => {
        onChange?.((value || []).filter((id) => id !== fid));
        setThumbs((p) => {
            const t = p.find((x) => x.file_id === fid);
            if (t?.previewUrl) URL.revokeObjectURL(t.previewUrl);
            return p.filter((x) => x.file_id !== fid);
        });
    };

    const count = (value || []).length;

    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-foreground">{label}</label>
                <span className="text-[10px] text-muted-foreground">{count}/{max}</span>
            </div>

            {/* Thumbnail strip — only renders when there's something to show */}
            {thumbs.length > 0 && (
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {thumbs.map((t) => (
                        <div
                            key={t.file_id}
                            className="relative w-14 h-14 rounded-lg overflow-hidden border border-border bg-card group"
                            data-testid={`${testid}-thumb-${t.file_id}`}
                        >
                            {/* Local preview only — alt is descriptive */}
                            <img src={t.previewUrl} alt={t.name} className="w-full h-full object-cover" />
                            <button
                                type="button"
                                onClick={() => remove(t.file_id)}
                                disabled={disabled}
                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-rose-600 text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label={`Remove ${t.name}`}
                            >
                                <X size={9} weight="bold" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <button
                type="button"
                disabled={disabled || uploading || count >= max}
                onClick={() => inputRef.current?.click()}
                className="w-full border-2 border-dashed border-border rounded-lg py-3 px-3 hover:border-primary hover:bg-primary/[0.02] transition-all flex items-center justify-center gap-2 text-xs text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`${testid}-trigger`}
            >
                {uploading ? (
                    <>
                        <Spinner size={14} className="animate-spin" />
                        Uploading…
                    </>
                ) : count >= max ? (
                    <>
                        <ImageIcon size={14} weight="duotone" />
                        Max {max} photos attached
                    </>
                ) : (
                    <>
                        <Camera size={14} weight="duotone" />
                        {count === 0 ? "Tap to attach photos" : `Add ${max - count} more`}
                    </>
                )}
            </button>
            {helperText && (
                <p className="text-[10px] text-muted-foreground mt-1 leading-snug">{helperText}</p>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => upload(e.target.files)}
                className="hidden"
                data-testid={`${testid}-input`}
            />
        </div>
    );
}
